import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime } from "../utils/datetime";

type Row = {
  id: number;
  from_addr: string;
  subject: string;
  ticket_id: number | null;
  customer_id: number | null;
  action: "created" | "appended" | "dropped" | "error";
  error_message: string | null;
  received_at: string | null;
  processed_at: string;
};

const ACTION_COLORS: Record<string, string> = {
  created: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  appended: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  dropped: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  blocked: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  error: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

// Names the route mail actually takes. The banner used to be a bare
// configured/not-configured flag driven by IMAP alone, so it read "Inbound
// mailbox not configured" on an appliance that was receiving its own mail and
// filing tickets from it in the table directly below.
const INBOUND_PATH_LABEL: Record<string, string> = {
  receiver: "Receiving mail directly on port 25",
  relay: "Receiving mail via the signed relay endpoint",
  imap: "Receiving mail by polling a provider mailbox",
};

type BlockRule = {
  id: number;
  pattern: string;
  reason: string | null;
  active: boolean;
  hit_count: number;
  last_hit_at: string | null;
  created_at: string | null;
  match_type: "exact" | "domain";
};

export default function EmailIntake() {
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [inboundPath, setInboundPath] = useState<string | null>(null);
  const [intakeAddr, setIntakeAddr] = useState<string | null>(null);
  // Why there is no intake address, when there is not one. "Configured" next
  // to an empty address leaves the operator guessing which half is lying.
  const [intakeReason, setIntakeReason] = useState<string | null>(null);
  const [lastPoll, setLastPoll] = useState<any>(null);
  const logSort = useSort("processed_at", "desc");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Email intake log",
    useMemo(
      () =>
        rows.map((r: any) => ({
          from: r.from_address ?? null,
          subject: r.subject ?? null,
          outcome: r.outcome ?? r.result ?? null,
          ticket_id: r.ticket_id ?? null,
          processed_at: r.processed_at ?? null,
          // NOT the message body - an inbound email can contain anything a
          // customer chose to type, including a password they should not
          // have sent us.
        })),
      [rows],
    ),
    undefined,
  );

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/api/v1/email-intake/status"),
        api.get("/api/v1/email-intake", {
          params: filter ? { action: filter, limit: 200 } : { limit: 200 },
        }),
      ]);
      setConfigured(!!s.data.configured);
      setInboundPath(s.data.inbound_path || null);
      setIntakeAddr(s.data.intake_address || null);
      setIntakeReason(s.data.intake_reason || null);
      setRows(l.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Load failed");
    } finally {
      setBusy(false);
    }
  }, [filter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pollNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/api/v1/email-intake/poll");
      setLastPoll(r.data);
      await refresh();
    } catch (e: any) {
      alert("Poll failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Intake</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Inbound support emails get auto-converted to tickets. Replies with{" "}
            <code>[Ticket #N]</code> in the subject become comments on the existing ticket and
            reopen it if closed.
          </p>
        </div>
        <div className="flex gap-2">
          {["", "created", "appended", "dropped", "error"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              className={`px-3 py-1 rounded text-sm ${
                filter === s
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
              }`}
            >
              {s || "all"}
            </button>
          ))}
          <button
            onClick={pollNow}
            disabled={busy}
            className="px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            Poll now
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded p-3 text-sm dark:text-gray-100 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>
          Status:{" "}
          {configured === null ? (
            "…"
          ) : configured ? (
            <span className="text-green-700 dark:text-green-400 font-medium">
              {INBOUND_PATH_LABEL[inboundPath || ""] || "Receiving mail"}
            </span>
          ) : (
            <span className="text-amber-700 dark:text-amber-400 font-medium">
              No inbound route — email can't create tickets yet
            </span>
          )}
        </span>
        {/* Configuration lives in one place: HelpDesk -> Setup. This page is
            the processing log. */}
        <Link
          to="/helpdesk/setup"
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          {configured ? "Mail settings" : "Set up receiving"}
        </Link>
        {intakeAddr && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Ticket replies route to <span className="font-mono">{intakeAddr}</span>
          </span>
        )}
        {!intakeAddr && intakeReason && (
          <span className="text-xs text-amber-700 dark:text-amber-400">
            {intakeReason}
          </span>
        )}
        {lastPoll && (
          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
            last poll: created={lastPoll.created} appended={lastPoll.appended} skipped=
            {lastPoll.skipped} errors={lastPoll.errors}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <BlockRulesSection />

      <div className="bg-white dark:bg-gray-800 rounded border dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="From" sortKey="from_addr" sort={logSort.sort} onSort={logSort.toggle} hide="hidden md:table-cell" />
              <SortableTh label="Subject" sortKey="subject" sort={logSort.sort} onSort={logSort.toggle} />
              <SortableTh label="Customer" sortKey="customer_id" sort={logSort.sort} onSort={logSort.toggle} hide="hidden lg:table-cell" />
              <SortableTh label="Ticket" sortKey="ticket_id" sort={logSort.sort} onSort={logSort.toggle} />
              <SortableTh label="Action" sortKey="action" sort={logSort.sort} onSort={logSort.toggle} hide="hidden sm:table-cell" />
              <SortableTh label="Processed" sortKey="processed_at" sort={logSort.sort} onSort={logSort.toggle} hide="hidden md:table-cell" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-gray-400 dark:text-gray-500">
                  No emails processed yet.
                </td>
              </tr>
            ) : (
              sortRows(rows, logSort.sort, (r, key) => {
                switch (key) {
                  case "from_addr": return (r.from_addr || "").toLowerCase();
                  case "subject": return (r.subject || "").toLowerCase();
                  case "customer_id": return r.customer_id ?? 0;
                  case "ticket_id": return r.ticket_id ?? 0;
                  case "action": return (r.action || "").toLowerCase();
                  case "processed_at": return r.processed_at ? Date.parse(r.processed_at) : 0;
                  default: return 0;
                }
              }).map((r) => (
                <tr key={r.id} className="border-b dark:border-gray-700 last:border-0 dark:text-gray-100">
                  <td className="py-2 px-3 font-mono text-xs hidden md:table-cell">{r.from_addr}</td>
                  <td className="py-2 px-3 max-w-md truncate">{r.subject}</td>
                  <td className="py-2 px-3 text-xs hidden lg:table-cell">
                    {r.customer_id ? (
                      <Link
                        to={`/customers/${r.customer_id}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        #{r.customer_id}
                      </Link>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs">
                    {r.ticket_id ? (
                      <Link
                        to={`/tickets/${r.ticket_id}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        #{r.ticket_id}
                      </Link>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3 hidden sm:table-cell">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        ACTION_COLORS[r.action] || "bg-gray-100 dark:bg-gray-700 dark:text-gray-200"
                      }`}
                      title={r.error_message || ""}
                    >
                      {r.action}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-gray-500 dark:text-gray-400 hidden md:table-cell">
                    {formatDateTime(r.processed_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BlockRulesSection() {
  const [rules, setRules] = useState<BlockRule[]>([]);
  const [pattern, setPattern] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const blockedSort = useSort("pattern", "asc");

  const load = async () => {
    setErr(null);
    try {
      const r = await api.get("/api/v1/email-intake/block-rules");
      setRules(r.data || []);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Failed to load block rules");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!pattern.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      await api.post("/api/v1/email-intake/block-rules", {
        pattern: pattern.trim(),
        reason: reason.trim() || null,
        active: true,
      });
      setPattern("");
      setReason("");
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Failed to add rule");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r: BlockRule) => {
    setBusy(true);
    try {
      await api.patch(`/api/v1/email-intake/block-rules/${r.id}`, {
        active: !r.active,
      });
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Failed to update rule");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: BlockRule) => {
    if (
      !window.confirm(
        `Delete block rule for "${r.pattern}"? Messages from this sender will create tickets again.`,
      )
    )
      return;
    setBusy(true);
    try {
      await api.delete(`/api/v1/email-intake/block-rules/${r.id}`);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Failed to delete rule");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded p-4 dark:text-gray-100">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <h2 className="text-base font-semibold">Blocked senders</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Senders matched against this list are silently dropped — no ticket
            opens, the message is logged in the table below as{" "}
            <code>blocked</code>. Use <code>user@example.com</code> for an exact
            address, or <code>@example.com</code> for the whole domain.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-end mb-3">
        <label className="text-xs flex-1 min-w-[180px]">
          <span className="block text-gray-600 dark:text-gray-400 mb-1">Pattern</span>
          <input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="@spammer.com  or  noreply@vendor.com"
            className="w-full border rounded px-2 py-1.5 font-mono text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
          />
        </label>
        <label className="text-xs flex-[2] min-w-[240px]">
          <span className="block text-gray-600 dark:text-gray-400 mb-1">Reason (optional)</span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="why this is blocked"
            className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
          />
        </label>
        <button
          onClick={add}
          disabled={saving || !pattern.trim()}
          className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-sm font-medium disabled:opacity-50"
        >
          {saving ? "Adding…" : "+ Add rule"}
        </button>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded text-xs mb-2">
          {err}
        </div>
      )}

      {rules.length === 0 ? (
        <div className="text-xs text-gray-400 dark:text-gray-500 italic">
          No block rules yet. Add one above to suppress noisy senders.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Pattern" sortKey="pattern" sort={blockedSort.sort} onSort={blockedSort.toggle} />
              <SortableTh label="Type" sortKey="match_type" sort={blockedSort.sort} onSort={blockedSort.toggle} hide="hidden sm:table-cell" />
              <SortableTh label="Reason" sortKey="reason" sort={blockedSort.sort} onSort={blockedSort.toggle} hide="hidden lg:table-cell" />
              <SortableTh label="Hits" sortKey="hit_count" sort={blockedSort.sort} onSort={blockedSort.toggle} hide="hidden md:table-cell" />
              <SortableTh label="Last hit" sortKey="last_hit_at" sort={blockedSort.sort} onSort={blockedSort.toggle} hide="hidden md:table-cell" />
              <SortableTh label="Active" sortKey="active" sort={blockedSort.sort} onSort={blockedSort.toggle} />
              <th className="py-1.5 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {sortRows(rules, blockedSort.sort, (r, key) => {
              switch (key) {
                case "pattern": return (r.pattern || "").toLowerCase();
                case "match_type": return (r.match_type || "").toLowerCase();
                case "reason": return (r.reason || "").toLowerCase();
                case "hit_count": return r.hit_count;
                case "last_hit_at": return r.last_hit_at ? Date.parse(r.last_hit_at) : 0;
                case "active": return r.active ? 1 : 0;
                default: return 0;
              }
            }).map((r) => (
              <tr key={r.id} className="border-b dark:border-gray-700 last:border-0">
                <td className="py-1.5 px-2 font-mono text-xs">{r.pattern}</td>
                <td className="py-1.5 px-2 text-xs hidden sm:table-cell">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] ${
                      r.match_type === "domain"
                        ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"
                        : "bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200"
                    }`}
                  >
                    {r.match_type}
                  </span>
                </td>
                <td className="py-1.5 px-2 text-xs text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                  {r.reason || "—"}
                </td>
                <td className="py-1.5 px-2 text-xs font-mono hidden md:table-cell">{r.hit_count}</td>
                <td className="py-1.5 px-2 text-xs text-gray-500 dark:text-gray-400 hidden md:table-cell">
                  {r.last_hit_at
                    ? formatDateTime(r.last_hit_at)
                    : "—"}
                </td>
                <td className="py-1.5 px-2">
                  <button
                    onClick={() => toggleActive(r)}
                    disabled={busy}
                    className={`text-xs px-2 py-0.5 rounded ${
                      r.active
                        ? "bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/60"
                        : "bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    }`}
                  >
                    {r.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="py-1.5 px-2 text-right">
                  <button
                    onClick={() => remove(r)}
                    className="text-xs text-red-600 dark:text-red-400 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
